import React from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    Chip
} from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const FreteGratisPagination = ({ 
    pagination, 
    onPageChange, 
    onRowsPerPageChange,
    rowsPerPageOptions = [10, 20, 50, 100] 
}) => {
    if (!pagination) return null;

    const {
        current_page,
        per_page,
        total,
        total_pages,
        has_next,
        has_prev
    } = pagination;

    const handleFirstPage = () => {
        onPageChange(1);
    };

    const handlePrevPage = () => {
        onPageChange(current_page - 1);
    };

    const handleNextPage = () => {
        onPageChange(current_page + 1);
    };

    const handleLastPage = () => {
        onPageChange(total_pages);
    };

    const handleRowsPerPageChange = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        onRowsPerPageChange(newRowsPerPage);
    };

    const startItem = ((current_page - 1) * per_page) + 1;
    const endItem = Math.min(current_page * per_page, total);

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                bgcolor: 'white',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                mb: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}
        >
            {/* Informações de registros */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ 
                        color: '#495057', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                    }}>
                        Total:
                    </Typography>
                    <Chip
                        label={total}
                        size="small"
                        sx={{
                            bgcolor: '#e7f3ff',
                            color: '#0066cc',
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            height: 24
                        }}
                    />
                </Box>
                
                <Typography variant="body2" sx={{ 
                    color: '#6c757d',
                    fontSize: '0.875rem'
                }}>
                    Exibindo {startItem}-{endItem}
                </Typography>

                {total_pages > 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ 
                            color: '#495057', 
                            fontWeight: 500,
                            fontSize: '0.875rem'
                        }}>
                            Página:
                        </Typography>
                        <Chip
                            label={`${current_page} de ${total_pages}`}
                            size="small"
                            variant="outlined"
                            sx={{
                                borderColor: '#dee2e6',
                                color: '#495057',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                height: 24
                            }}
                        />
                    </Box>
                )}
            </Box>

            {/* Controles de paginação */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Seletor de itens por página */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="body2" sx={{ 
                        color: '#495057', 
                        fontSize: '0.875rem',
                        fontWeight: 500
                    }}>
                        Por página:
                    </Typography>
                    <FormControl size="small">
                        <Select
                            value={per_page}
                            onChange={handleRowsPerPageChange}
                            sx={{
                                minWidth: 80,
                                height: 36,
                                fontSize: '0.875rem',
                                bgcolor: 'white',
                                border: '1px solid #dee2e6',
                                '& .MuiSelect-select': {
                                    py: 0.8,
                                    fontWeight: 500
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none'
                                }
                            }}
                        >
                            {rowsPerPageOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Botões de navegação */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                        onClick={handleFirstPage}
                        disabled={!has_prev}
                        size="small"
                        sx={{
                            bgcolor: 'white',
                            border: '1px solid #dee2e6',
                            width: 36,
                            height: 36,
                            '&:hover': {
                                bgcolor: '#f8f9fa',
                                borderColor: '#adb5bd'
                            },
                            '&.Mui-disabled': {
                                bgcolor: '#f8f9fa',
                                borderColor: '#e9ecef',
                                color: '#adb5bd'
                            }
                        }}
                    >
                        <FirstPageIcon fontSize="small" />
                    </IconButton>
                    
                    <IconButton
                        onClick={handlePrevPage}
                        disabled={!has_prev}
                        size="small"
                        sx={{
                            bgcolor: 'white',
                            border: '1px solid #dee2e6',
                            width: 36,
                            height: 36,
                            '&:hover': {
                                bgcolor: '#f8f9fa',
                                borderColor: '#adb5bd'
                            },
                            '&.Mui-disabled': {
                                bgcolor: '#f8f9fa',
                                borderColor: '#e9ecef',
                                color: '#adb5bd'
                            }
                        }}
                    >
                        <NavigateBeforeIcon fontSize="small" />
                    </IconButton>
                    
                    <Box sx={{ 
                        px: 2.5, 
                        py: 1, 
                        bgcolor: '#0066cc', 
                        borderRadius: 1.5,
                        minWidth: 40,
                        textAlign: 'center'
                    }}>
                        <Typography variant="body2" sx={{ 
                            color: 'white', 
                            fontWeight: 600,
                            fontSize: '0.875rem'
                        }}>
                            {current_page}
                        </Typography>
                    </Box>
                    
                    <IconButton
                        onClick={handleNextPage}
                        disabled={!has_next}
                        size="small"
                        sx={{
                            bgcolor: 'white',
                            border: '1px solid #dee2e6',
                            width: 36,
                            height: 36,
                            '&:hover': {
                                bgcolor: '#f8f9fa',
                                borderColor: '#adb5bd'
                            },
                            '&.Mui-disabled': {
                                bgcolor: '#f8f9fa',
                                borderColor: '#e9ecef',
                                color: '#adb5bd'
                            }
                        }}
                    >
                        <NavigateNextIcon fontSize="small" />
                    </IconButton>
                    
                    <IconButton
                        onClick={handleLastPage}
                        disabled={!has_next}
                        size="small"
                        sx={{
                            bgcolor: 'white',
                            border: '1px solid #dee2e6',
                            width: 36,
                            height: 36,
                            '&:hover': {
                                bgcolor: '#f8f9fa',
                                borderColor: '#adb5bd'
                            },
                            '&.Mui-disabled': {
                                bgcolor: '#f8f9fa',
                                borderColor: '#e9ecef',
                                color: '#adb5bd'
                            }
                        }}
                    >
                        <LastPageIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
};

export default FreteGratisPagination;